import { z } from "zod";

/**
 * Shared DTO cho Admin SRE posture (US-AE9.F5-0032 · @UC9084 backup-dr-status · @UC9085
 * secrets-env-posture). Một nguồn sự thật cho api (S01), worker feed (S06) và admin web (S04).
 *
 * Hai bề mặt ĐỌC (READ-mostly) cho portal SRE:
 *  - Backup/DR posture  : trạng thái backup per môi trường (lastBackupAt, retention, restore-test,
 *    RPO/RTO). Engine backup thực thi là HẠ TẦNG NGOÀI app — portal CHỈ ĐỌC. Thiếu pipeline →
 *    status='unconfigured' (KHÔNG bịa số/SLA).
 *  - Secret posture     : trạng thái secret per (name, env) — present/rotatedAt/dueAt.
 *
 * ═══ BẤT BIẾN CỨNG (G4 — secret KHÔNG BAO GIỜ lộ giá trị) ═══
 * `SecretPostureItem` CHỈ chứa metadata: name (định danh env var) + env + present (Boolean) +
 * rotatedAt + dueAt + status. TUYỆT ĐỐI KHÔNG field value/plaintext/secret. Schema `.strict()` chặn
 * field lạ leo vào. Reviewer security + test int-spec assert response KHÔNG chứa giá trị secret.
 *
 * Invariants khác:
 * - C1 no-SMS: cảnh báo posture (worker S06) chỉ teams/email (AlertTransport enforce).
 * - Không bịa SLA/retention khi thiếu nguồn (NULL → status 'unconfigured').
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin: apps/admin/src/lib/status.ts)
// ============================================================

/** Môi trường triển khai (khớp badge STATUS: prod/staging + dev). */
export const sreEnvSchema = z.enum(["dev", "staging", "prod"]);
export type SreEnv = z.infer<typeof sreEnvSchema>;

/**
 * Trạng thái backup/DR (khớp badge STATUS): ok (backup gần đây OK) | stale (quá ngưỡng — cảnh báo) |
 * failed (backup lỗi) | unconfigured (chưa có pipeline — KHÔNG bịa số).
 */
export const backupStatusSchema = z.enum(["ok", "stale", "failed", "unconfigured"]);
export type BackupStatusCode = z.infer<typeof backupStatusSchema>;

/**
 * Trạng thái secret (khớp badge STATUS): present (env var có mặt) | missing (thiếu — cảnh báo) |
 * stale (present nhưng quá hạn rotation dueAt). KHÔNG bao giờ kèm giá trị.
 */
export const secretStatusSchema = z.enum(["present", "missing", "stale"]);
export type SecretStatusCode = z.infer<typeof secretStatusSchema>;

// ============================================================
// Backup/DR posture (READ) — @UC9084
// ============================================================

/**
 * Một dòng trạng thái backup/DR per môi trường. `overdue` = computed (backup quá ngưỡng stale) cho
 * AC3 cảnh báo. Thời gian ISO 8601; null khi chưa có. retention/rpo/rto null khi chưa cấu hình
 * (KHÔNG hardcode SLA). `source` = nhãn nguồn ('managed-pg'|'unconfigured') — KHÔNG secret/DSN.
 */
export const backupPostureItemSchema = z
  .object({
    env: sreEnvSchema,
    status: backupStatusSchema,
    source: z.string(),
    /** ISO 8601 lần backup gần nhất; null = chưa có backup. */
    lastBackupAt: z.string().datetime().nullable(),
    /** Số ngày lưu backup; null = chưa cấu hình. */
    retentionDays: z.number().int().nonnegative().nullable(),
    /** ISO 8601 lần restore-test gần nhất; null = chưa test. */
    lastRestoreTestAt: z.string().datetime().nullable(),
    /** Mục tiêu RPO (phút); null = chưa cấu hình. */
    rpoMinutes: z.number().int().nonnegative().nullable(),
    /** Mục tiêu RTO (phút); null = chưa cấu hình. */
    rtoMinutes: z.number().int().nonnegative().nullable(),
    /** Computed: backup quá ngưỡng stale hoặc failed → cần chú ý (AC3 cảnh báo). */
    overdue: z.boolean(),
  })
  .strict();
export type BackupPostureItem = z.infer<typeof backupPostureItemSchema>;

/**
 * Response GET /admin/sre/backups — danh sách posture per môi trường + cờ tổng `anyOverdue`
 * (AC3 — có ≥1 env cần chú ý). Không phân trang (số môi trường nhỏ, bounded).
 */
export const backupPostureResponseSchema = z
  .object({
    data: z.array(backupPostureItemSchema),
    anyOverdue: z.boolean(),
  })
  .strict();
export type BackupPostureResponse = z.infer<typeof backupPostureResponseSchema>;

// ============================================================
// Secret posture (READ) — @UC9085 — G4: KHÔNG field value
// ============================================================

/**
 * Một dòng trạng thái secret. G4 CỨNG: CHỈ metadata — name + env + present + rotatedAt + dueAt +
 * status. TUYỆT ĐỐI KHÔNG field value/plaintext. `.strict()` chặn field lạ. `status` = computed:
 * present | missing | stale (present nhưng quá dueAt).
 */
export const secretPostureItemSchema = z
  .object({
    /** Định danh env var (vd 'JWT_REFRESH_SECRET') — KHÔNG giá trị (G4). */
    name: z.string(),
    env: sreEnvSchema,
    /** env var có mặt (process.env set) — Boolean, KHÔNG giá trị. */
    present: z.boolean(),
    /** ISO 8601 lần xoay vòng gần nhất; null = chưa rõ. */
    rotatedAt: z.string().datetime().nullable(),
    /** ISO 8601 hạn xoay vòng kế; null = không theo lịch. */
    dueAt: z.string().datetime().nullable(),
    status: secretStatusSchema,
  })
  .strict();
export type SecretPostureItem = z.infer<typeof secretPostureItemSchema>;

/**
 * Response GET /admin/secrets/status — danh sách posture secret + cờ tổng `anyMissing` (AC3 — có
 * ≥1 secret thiếu/stale cần chú ý). Có thể filter theo env qua query. G4: KHÔNG dòng nào lộ giá trị.
 */
export const secretPostureResponseSchema = z
  .object({
    data: z.array(secretPostureItemSchema),
    anyMissing: z.boolean(),
  })
  .strict();
export type SecretPostureResponse = z.infer<typeof secretPostureResponseSchema>;

// ============================================================
// Query (READ) — filter theo env (tuỳ chọn)
// ============================================================

/** Query cho GET /admin/secrets/status + GET /admin/sre/backups — filter env tuỳ chọn. */
export const srePostureQuerySchema = z
  .object({
    env: sreEnvSchema.optional(),
  })
  .strict();
export type SrePostureQuery = z.infer<typeof srePostureQuerySchema>;

/**
 * Danh sách KEY bị CẤM xuất hiện trong SecretPostureItem (G4 — dùng ở test guard client + server).
 * Nếu bất kỳ key nào lọt vào response → vi phạm bất biến (secret value bị lộ).
 */
export const SECRET_FORBIDDEN_KEYS: readonly string[] = [
  "value",
  "plaintext",
  "secret",
  "secretValue",
  "raw",
  "rawValue",
];
